import { useState } from 'react';

import RequestPasswordReset from '@/auth/components/RequestPasswordReset';
import SetPassword from '@/auth/components/SetPassword';
import { authSession } from '@/auth/utils/authSession';
import { cognitoPasswordLogin } from '@/auth/utils/cognitoLogin';
import {
  confirmCognitoPasswordReset,
  requestCognitoPasswordReset,
} from '@/auth/utils/cognitoPassword';
import { isCognitoLoginEnabled, isCognitoSessionActive } from '@/auth/utils/cognitoSession';
import { MFA_SETUP_PATH } from '@/auth/utils/mfaGate';
import { ERROR_CODES, hasErrorCode } from '@/core/api';

import CognitoPasswordReset, { ResetActions } from './CognitoPasswordReset';

interface ResetPasswordFlowProps {
  /** A legacy emailed `password_token`, if the page was opened from one. */
  token?: string;
  /** Sanitized same-origin destination after the reset. */
  next: string;
}

/**
 * /password_reset (ENG-6104). The flow is decided at render and again right
 * before the first request, since `cognito-auth` can resolve after the
 * bounded boot wait; once a request is made the flow stays put.
 */
export default function ResetPasswordFlow({ token, next }: ResetPasswordFlowProps): JSX.Element {
  const [cognito, setCognito] = useState(isCognitoLoginEnabled);
  const [sentTo, setSentTo] = useState<string>();

  const actions: ResetActions = {
    requestCode: requestCognitoPasswordReset,
    confirmReset: confirmCognitoPasswordReset,
    signIn: async (email, password) => {
      // A legacy session from before the reset would outlive a sign-in that stops at MFA, and
      // the reload into Login would restore it. Cognito state is left alone; the sign-in owns it.
      authSession.clearToken();
      try {
        // Opts out of the MFA gate redirect, which would resume at this page.
        await cognitoPasswordLogin({ email, password });
      } catch (err) {
        // Signed in, but the api wants MFA enrolment first: enrol, then go on to `next`.
        // Without a session (a Cognito MFA_SETUP challenge) the login page enrols instead.
        if (hasErrorCode(err, ERROR_CODES.AUTH_MFA_SETUP_REQUIRED) && isCognitoSessionActive()) {
          window.location.assign(`${MFA_SETUP_PATH}?next=${encodeURIComponent(next)}`);
          return;
        }
        throw err;
      }
      window.location.assign(next);
    },
  };

  async function takeOver(email: string): Promise<boolean> {
    if (!isCognitoLoginEnabled()) return false;
    await actions.requestCode(email);
    setSentTo(email.trim());
    setCognito(true);
    return true;
  }

  if (cognito) {
    return (
      <CognitoPasswordReset
        actions={actions}
        legacyLink={!!token}
        loginHref={`/login?next=${encodeURIComponent(next)}`}
        sentTo={sentTo}
      />
    );
  }
  // A legacy link means nothing to Cognito: switch to the code request instead of using it.
  function takeOverTokenReset(): boolean {
    if (!isCognitoLoginEnabled()) return false;
    setCognito(true);
    return true;
  }

  return token ? (
    <SetPassword token={token} takeOverTokenReset={takeOverTokenReset} />
  ) : (
    <RequestPasswordReset takeOver={takeOver} />
  );
}
